import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { KeyboardNavigation } from "./KeyboardNavigation";

export class ChoiceGridRendererNavigation extends KeyboardNavigation {
    // variableIds will be computed dynamically from defaultFocusStatus.statuses
    // to reflect the actual rendered order (supports Permutation/randomization).

    getVariableIdsForQuestion(questionId: number) {
        return this.defaultFocusStatus.statuses
            .filter((s) => s.questionId === questionId)
            .map((s) => s.variableId)
            .filter((v, i, arr) => arr.indexOf(v) === i);
    }

    findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        if (this.isHybridGrid && (eventKey === "ArrowLeft" || eventKey === "ArrowRight")) {
            return this.findNextFocusableElementWithinSameVariableCommonForHybridGrid(
                currentElement,
                eventKey,
            );
        }

        if (this.isHybridGrid && (eventKey === "ArrowDown" || eventKey === "ArrowUp")) {
            return this.findNextFocusableElementWithinSameVariableCommonForSwitchingRowsForHybridGrid(
                currentElement,
                eventKey,
            );
        }

        // Compute variableIds in DOM/render order for this question so navigation follows
        // any runtime permutation/shuffle applied to columns/rows.
        const variableIds = this.getVariableIdsForQuestion(currentElement.questionId);

        let nextVariableId = currentElement.variableId;
        const currentVariableIndex = variableIds.indexOf(currentElement.variableId);

        // Helper to check if an element is visible
        // Helper to check if a specific element DOM node is visible.
        // If elementRef.current is missing, try finding it by id.
        const isDomNodeVisible = (el) => {
            if (!el) return false;
            const node = el.current ? el.current : document.getElementById(el.id || el.elementId);
            if (!node) return false;
            const style = window.getComputedStyle(node);
            return style.display !== "none" && style.visibility !== "hidden";
        };

        // Find next visible row for ArrowDown
        if (eventKey === "ArrowDown") {
            for (let nextIdx = currentVariableIndex + 1; nextIdx < variableIds.length; nextIdx++) {
                const nextVarId = variableIds[nextIdx];
                for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
                    const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                    if (
                        i > index &&
                        elementFocusStatus.variableId === nextVarId &&
                        elementFocusStatus.elementId !== currentElement.elementId &&
                        isDomNodeVisible(
                            elementFocusStatus.elementRef
                                ? elementFocusStatus.elementRef
                                : {
                                      id: elementFocusStatus.elementId,
                                      elementId: elementFocusStatus.elementId,
                                  },
                        )
                    ) {
                        return elementFocusStatus;
                    }
                }
            }
            return null;
        }
        // Find previous visible row for ArrowUp
        if (eventKey === "ArrowUp") {
            for (let prevIdx = currentVariableIndex - 1; prevIdx >= 0; prevIdx--) {
                const prevVarId = variableIds[prevIdx];

                for (let i = this.defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                    const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                    if (
                        i < index &&
                        elementFocusStatus.variableId === prevVarId &&
                        elementFocusStatus.elementId !== currentElement.elementId &&
                        isDomNodeVisible(
                            elementFocusStatus.elementRef
                                ? elementFocusStatus.elementRef
                                : {
                                      id: elementFocusStatus.elementId,
                                      elementId: elementFocusStatus.elementId,
                                  },
                        )
                    ) {
                        return elementFocusStatus;
                    }
                }
            }
            return null;
        }
        // Horizontal navigation remains unchanged
        if (eventKey === "ArrowRight") {
            for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    i > index &&
                    elementFocusStatus.variableId === nextVariableId &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    isDomNodeVisible(
                        elementFocusStatus.elementRef
                            ? elementFocusStatus.elementRef
                            : {
                                  id: elementFocusStatus.elementId,
                                  elementId: elementFocusStatus.elementId,
                              },
                    )
                ) {
                    return elementFocusStatus;
                }
            }
        } else if (eventKey === "ArrowLeft") {
            for (let i = this.defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    i < index &&
                    elementFocusStatus.variableId === nextVariableId &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    isDomNodeVisible(
                        elementFocusStatus.elementRef
                            ? elementFocusStatus.elementRef
                            : {
                                  id: elementFocusStatus.elementId,
                                  elementId: elementFocusStatus.elementId,
                              },
                    )
                ) {
                    return elementFocusStatus;
                }
            }
        }

        return null;
    }

    findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const variableIds = this.getVariableIdsForQuestion(currentElement.questionId);
        return this.findNextFocusableElementWithinNextVariableCommon(currentElement, variableIds);
    }

    findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const variableIds = this.getVariableIdsForQuestion(currentElement.questionId);
        return this.findLastFocusableElementWithinPreviousVariableCommon(
            currentElement,
            variableIds,
        );
    }
}
